import React from 'react'
import { NavLink } from 'react-router-dom'

// Utils
import { workspaceSettingsPermissions } from 'modules/workspace/utils'

// Constants
import { routes } from '@sketch/constants'

// Hooks
import { useIsProfileEnabled } from 'modules/workspaceProfile/hooks/useIsProfileEnabled'

// Compoments
import Navbar from 'components/Navbar'
import { Segment } from '@sketch/components'

// Local Compoments
import WorkspaceSidebarLayout from '../WorkspaceSidebarLayout'

// Styles
import { ContentWrapper } from './WorkspaceSettingsLayout.styles'

// Types
import { WorkspaceMinimalFragment } from '@sketch/gql-types'

interface WorkspaceSettingsLayoutProps {
  workspace: WorkspaceMinimalFragment
  workspaces: WorkspaceMinimalFragment[]
  canUseSso?: boolean
  hideNavbar: boolean
  canRender: {
    generalMenu: boolean
    billingMenu: boolean
    SSOMenu: boolean
  }
}

const WorkspaceSettingsLayout = React.forwardRef<
  HTMLElement,
  React.PropsWithChildren<WorkspaceSettingsLayoutProps>
>(function WorkspaceSettingsLayout(props, ref) {
  const { workspace, canRender, children, workspaces, hideNavbar } = props

  const { name } = workspace
  const workspaceId = workspace.identifier

  const { isProfileEnabled } = useIsProfileEnabled(workspaceId)

  const { isAdmin } = workspaceSettingsPermissions({
    workspace,
  })

  const title = name && `${name} Workspace Settings`

  return (
    <WorkspaceSidebarLayout
      title={title}
      currentWorkspace={workspace}
      workspaces={workspaces}
      navbar={
        hideNavbar ? null : (
          <Navbar.Section ml={2} data-testid="workspace-settings-navbar">
            <Segment
              type="text"
              label="People"
              render={(className, children) => (
                <NavLink
                  className={className}
                  to={routes.WORKSPACE_SETTINGS_PEOPLE.create({
                    workspaceId,
                  })}
                >
                  {children}
                </NavLink>
              )}
            />
            {canRender.generalMenu && (
              <Segment
                type="text"
                label="Settings"
                render={(className, children) => (
                  <NavLink
                    className={className}
                    to={routes.WORKSPACE_SETTINGS_SETTINGS.create({
                      workspaceId,
                    })}
                  >
                    {children}
                  </NavLink>
                )}
              />
            )}
            {isProfileEnabled && isAdmin && (
              <Segment
                type="text"
                label="Profile"
                render={(className, children) => (
                  <NavLink
                    className={className}
                    to={routes.WORKSPACE_SETTINGS_PROFILE.create({
                      workspaceId,
                    })}
                  >
                    {children}
                  </NavLink>
                )}
              />
            )}
            {canRender.billingMenu && (
              <Segment
                type="text"
                label="Billing"
                render={(className, children) => (
                  <NavLink
                    className={className}
                    to={routes.WORKSPACE_SETTINGS_BILLING.create({
                      workspaceId,
                    })}
                  >
                    {children}
                  </NavLink>
                )}
              />
            )}
            {canRender.SSOMenu && (
              <Segment
                type="text"
                label="Single Sign-On"
                render={(className, children) => (
                  <NavLink
                    className={className}
                    to={routes.WORKSPACE_SETTINGS_SSO.create({
                      workspaceId,
                    })}
                  >
                    {children}
                  </NavLink>
                )}
              />
            )}
          </Navbar.Section>
        )
      }
    >
      <ContentWrapper>{children}</ContentWrapper>
    </WorkspaceSidebarLayout>
  )
})

export default WorkspaceSettingsLayout
